import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DistributionNetworkApi from "../../../../../infrastructure/api/distributionNetworkApi";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const CONTACT_TYPES = [
  { value: 1, label: "مدیر" },
  { value: 2, label: "مدیر واحد" },
  { value: 3, label: "کارشناس فنی" },
  { value: 4, label: "کارشناس انرژی تجدیدپذیر" },
  { value: 5, label: "مسئول انشعاب" },
  { value: 6, label: "مسئول قرارداد" },
  { value: 7, label: "بازرس" },
  { value: 8, label: "سایر" },
];

const validationSchema = Yup.object().shape({
  distributionCompanyId: Yup.number().min(1, "شرکت توزیع الزامی است").required(),
  firstName: Yup.string().required("نام الزامی است"),
  lastName: Yup.string().required("نام خانوادگی الزامی است"),
});

const empty = {
  id: 0,
  distributionCompanyId: "",
  distributionAreaId: "",
  firstName: "",
  lastName: "",
  position: "",
  mobile: "",
  phone: "",
  email: "",
  contactType: 8,
  notes: "",
  status: 2,
  portalEnabled: false,
  portalIsCompanyAdmin: false,
  portalPassword: "",
  portalAreas: [],
};

const emptyPortalArea = {
  distributionAreaId: "",
  canAreaReview: true,
  canEngineeringReview: false,
};

const toPortalAreas = (list) =>
  (list || []).map((a) => ({
    distributionAreaId: String(a.distributionAreaId ?? a.DistributionAreaId),
    canAreaReview: Boolean(a.canAreaReview ?? a.CanAreaReview),
    canEngineeringReview: Boolean(
      a.canEngineeringReview ?? a.CanEngineeringReview
    ),
  }));

const validatePortal = (values) => {
  if (!values.portalEnabled) return null;
  if (!String(values.mobile || "").trim())
    return "برای حساب پورتال، شماره موبایل الزامی است (نام کاربری ورود و پیامک ارجاع)";
  if (values.portalIsCompanyAdmin) return null;
  const rows = values.portalAreas.filter((a) => Number(a.distributionAreaId) > 0);
  if (rows.length === 0) return "برای کارشناس امور حداقل یک امور انتخاب کنید";
  const ids = rows.map((a) => a.distributionAreaId);
  if (new Set(ids).size !== ids.length) return "یک امور دو بار انتخاب شده است";
  if (rows.some((a) => !a.canAreaReview && !a.canEngineeringReview))
    return "برای هر امور حداقل یکی از دسترسی‌های ناظر امور یا واحد مهندسی را فعال کنید";
  return null;
};

const DistributionContactModifyPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isCreate = !id || id === "create";
  const navigate = useNavigate();
  const presetCompanyId = searchParams.get("companyId")
    ? Number(searchParams.get("companyId"))
    : "";
  const presetAreaId = searchParams.get("areaId")
    ? Number(searchParams.get("areaId"))
    : "";
  const [initial, setInitial] = useState({
    ...empty,
    distributionCompanyId: presetCompanyId,
    distributionAreaId: presetAreaId,
  });
  const [companies, setCompanies] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linkedToPortal, setLinkedToPortal] = useState(false);

  const loadAreas = async (companyId) => {
    if (!companyId) {
      setAreas([]);
      return;
    }
    const res = await DistributionNetworkApi.getAreaLookups(companyId);
    setAreas(res.data || []);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const lookups = await DistributionNetworkApi.getCompanyLookups();
      if (!cancelled) setCompanies(lookups.data || []);
      if (!isCreate) {
        const res = await DistributionNetworkApi.getContact(id);
        if (!cancelled) {
          if (res.ok && res.data) {
            const hasPortal = Boolean(res.data.hasPortalAccess);
            setInitial({
              ...empty,
              ...res.data,
              distributionAreaId: res.data.distributionAreaId || "",
              portalEnabled: hasPortal,
              portalIsCompanyAdmin: Boolean(res.data.portalIsCompanyAdmin),
              portalPassword: "",
              portalAreas: toPortalAreas(res.data.portalAreas),
            });
            setLinkedToPortal(hasPortal || Boolean(res.data.userId));
            await loadAreas(res.data.distributionCompanyId);
          } else toast.error(res.message || "مخاطب یافت نشد");
        }
      } else if (presetCompanyId) {
        await loadAreas(presetCompanyId);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isCreate, presetCompanyId]);

  const onSubmit = async (values) => {
    const portalError = validatePortal(values);
    if (portalError) {
      toast.error(portalError);
      return;
    }
    const {
      portalEnabled,
      portalIsCompanyAdmin,
      portalPassword,
      portalAreas,
      ...contact
    } = values;
    setSaving(true);
    try {
      const res = await DistributionNetworkApi.saveContact({
        ...contact,
        id: isCreate ? 0 : Number(id),
        distributionCompanyId: Number(values.distributionCompanyId),
        distributionAreaId: values.distributionAreaId
          ? Number(values.distributionAreaId)
          : null,
        contactType: Number(values.contactType),
        status: Number(values.status),
        portalAccess: {
          enabled: Boolean(portalEnabled),
          isCompanyAdmin: Boolean(portalIsCompanyAdmin),
          isActive: Number(values.status) === 2,
          password: portalPassword?.trim() || null,
          areas: portalIsCompanyAdmin
            ? []
            : portalAreas
                .filter((a) => Number(a.distributionAreaId) > 0)
                .map((a) => ({
                  distributionAreaId: Number(a.distributionAreaId),
                  canAreaReview: Boolean(a.canAreaReview),
                  canEngineeringReview: Boolean(a.canEngineeringReview),
                })),
        },
      });
      if (res.ok) {
        toast.success(res.message || "ذخیره شد");
        navigate("/SLN/Management/DistributionContacts");
      } else toast.error(res.message || "خطا در ذخیره");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" p={4}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader title={isCreate ? "ایجاد مخاطب توزیع" : "ویرایش مخاطب توزیع"} />
          <CardContent>
            <Formik
              enableReinitialize
              initialValues={initial}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleSubmit,
                setFieldValue,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        name="distributionCompanyId"
                        label="شرکت توزیع"
                        value={values.distributionCompanyId || ""}
                        disabled={linkedToPortal}
                        onChange={async (e) => {
                          handleChange(e);
                          setFieldValue("distributionAreaId", "");
                          setFieldValue("portalAreas", []);
                          await loadAreas(e.target.value);
                        }}
                        error={Boolean(
                          touched.distributionCompanyId &&
                            errors.distributionCompanyId
                        )}
                        helperText={
                          (touched.distributionCompanyId &&
                            errors.distributionCompanyId) ||
                          (linkedToPortal
                            ? "این مخاطب به حساب پورتال متصل است؛ تغییر شرکت مجاز نیست"
                            : "")
                        }
                      >
                        {companies.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.title}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        name="distributionAreaId"
                        label="امور برق (اختیاری)"
                        value={values.distributionAreaId || ""}
                        onChange={handleChange}
                      >
                        <MenuItem value="">—</MenuItem>
                        {areas.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.title}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        name="contactType"
                        label="نوع مخاطب"
                        value={values.contactType}
                        onChange={handleChange}
                      >
                        {CONTACT_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="firstName"
                        label="نام"
                        value={values.firstName || ""}
                        onChange={handleChange}
                        error={Boolean(touched.firstName && errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="lastName"
                        label="نام خانوادگی"
                        value={values.lastName || ""}
                        onChange={handleChange}
                        error={Boolean(touched.lastName && errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="position"
                        label="سمت"
                        value={values.position || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="mobile"
                        label="موبایل"
                        value={values.mobile || ""}
                        onChange={handleChange}
                        required={values.portalEnabled}
                        helperText={
                          values.portalEnabled
                            ? "نام کاربری ورود به پورتال و دریافت پیامک ارجاع"
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="phone"
                        label="تلفن"
                        value={values.phone || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="email"
                        label="ایمیل"
                        value={values.email || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        name="status"
                        label="وضعیت"
                        value={values.status}
                        onChange={handleChange}
                      >
                        <MenuItem value={1}>پیش‌نویس</MenuItem>
                        <MenuItem value={2}>فعال</MenuItem>
                        <MenuItem value={3}>غیرفعال</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" fontWeight={700}>
                        دسترسی پورتال شرکت توزیع
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(values.portalEnabled)}
                            onChange={(e) => {
                              setFieldValue("portalEnabled", e.target.checked);
                              if (
                                e.target.checked &&
                                !values.portalIsCompanyAdmin &&
                                values.portalAreas.length === 0
                              )
                                setFieldValue("portalAreas", [
                                  {
                                    ...emptyPortalArea,
                                    distributionAreaId: values.distributionAreaId
                                      ? String(values.distributionAreaId)
                                      : "",
                                  },
                                ]);
                            }}
                          />
                        }
                        label="دارای حساب کاربری پورتال (ورود به کارتابل شرکت توزیع)"
                      />
                      {!values.portalEnabled && linkedToPortal && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          با ذخیره، دسترسی ورود این شخص به پورتال شرکت توزیع
                          غیرفعال می‌شود (اطلاعات مخاطب حفظ می‌شود).
                        </Alert>
                      )}
                    </Grid>
                    {values.portalEnabled && (
                      <>
                        <Grid item xs={12}>
                          <Alert severity="info">
                            {linkedToPortal
                              ? "این مخاطب همان کاربر پورتال شرکت توزیع است؛ نام، موبایل، وضعیت و دسترسی‌ها در هر دو بخش (مخاطبین و کاربران پورتال) یکسان نگه داشته می‌شود."
                              : "با ذخیره، برای این مخاطب حساب ورود ساخته می‌شود (نام کاربری = موبایل). اگر رمز وارد نشود، رمز پیش‌فرض 123456 است و اطلاعات ورود پیامک می‌شود. وضعیت «فعال» مخاطب، فعال بودن حساب را تعیین می‌کند."}
                          </Alert>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <RadioGroup
                            row
                            value={values.portalIsCompanyAdmin ? "admin" : "area"}
                            onChange={(e) => {
                              const isAdmin = e.target.value === "admin";
                              setFieldValue("portalIsCompanyAdmin", isAdmin);
                              if (!isAdmin && values.portalAreas.length === 0)
                                setFieldValue("portalAreas", [
                                  {
                                    ...emptyPortalArea,
                                    distributionAreaId: values.distributionAreaId
                                      ? String(values.distributionAreaId)
                                      : "",
                                  },
                                ]);
                            }}
                          >
                            <FormControlLabel
                              value="area"
                              control={<Radio />}
                              label="کارشناس امور / واحد مهندسی"
                            />
                            <FormControlLabel
                              value="admin"
                              control={<Radio />}
                              label="مدیر شرکت (دسترسی به همه امورها)"
                            />
                          </RadioGroup>
                        </Grid>
                        {!linkedToPortal && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              type="password"
                              autoComplete="new-password"
                              name="portalPassword"
                              label="رمز اولیه (اختیاری)"
                              value={values.portalPassword || ""}
                              onChange={handleChange}
                              helperText="خالی = رمز پیش‌فرض 123456"
                            />
                          </Grid>
                        )}
                        {!values.portalIsCompanyAdmin && (
                          <Grid item xs={12}>
                            <Stack spacing={1}>
                              {values.portalAreas.map((row, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <TextField
                                    select
                                    size="small"
                                    sx={{ minWidth: 240 }}
                                    label="امور"
                                    value={row.distributionAreaId || ""}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `portalAreas[${idx}].distributionAreaId`,
                                        e.target.value
                                      )
                                    }
                                  >
                                    {areas.map((a) => (
                                      <MenuItem key={a.id} value={String(a.id)}>
                                        {a.title}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={Boolean(row.canAreaReview)}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `portalAreas[${idx}].canAreaReview`,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label="ناظر امور"
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={Boolean(row.canEngineeringReview)}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `portalAreas[${idx}].canEngineeringReview`,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label="واحد مهندسی"
                                  />
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      setFieldValue(
                                        "portalAreas",
                                        values.portalAreas.filter(
                                          (_, i) => i !== idx
                                        )
                                      )
                                    }
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </Box>
                              ))}
                              <Box>
                                <Button
                                  size="small"
                                  disabled={!values.distributionCompanyId}
                                  onClick={() =>
                                    setFieldValue("portalAreas", [
                                      ...values.portalAreas,
                                      { ...emptyPortalArea },
                                    ])
                                  }
                                >
                                  افزودن امور
                                </Button>
                              </Box>
                            </Stack>
                          </Grid>
                        )}
                      </>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        name="notes"
                        label="یادداشت"
                        value={values.notes || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : null}
                      >
                        ذخیره
                      </Button>
                      <Button
                        sx={{ ml: 1 }}
                        onClick={() =>
                          navigate("/SLN/Management/DistributionContacts")
                        }
                      >
                        انصراف
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DistributionContactModifyPage;
